
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ItemDraft, ManagerDetails, SubmissionData, Campaign, Notification, Brand, SiteConfig, WorkflowLog, Aggregator, User } from './types';
import { AppStep, CampaignStatus, SubmissionStatus, WorkflowStage, UserRole, AdminView, Department, SubmissionType } from './enums';
import { INITIAL_CAMPAIGNS, INITIAL_BRANDS } from './constants';
import { StepIndicator } from './components/StepIndicator';
import { CampaignNote } from './components/CampaignNote';
import { BrandSelection } from './components/BrandSelection';
import { ItemForm } from './components/ItemForm';
import { IdentityConfirm } from './components/IdentityConfirm';
import { SuccessScreen } from './components/SuccessScreen';
import { ManagerSubmissionBundleView, BundleEntry } from './components/ManagerSubmissionBundleView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProposalForm } from './components/ProposalForm';
import { Dashboard } from './components/Dashboard';
import { UnifiedLogin } from './components/UnifiedLogin'; // Unified Login
import { AdminPortal } from './components/admin/AdminPortal';
import { ActiveCampaignGrid } from './components/ActiveCampaignGrid';
import { ToastContainer, ToastMessage } from './components/Toast';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { getToken, removeToken } from './utils/auth';
import { apiClient } from './services/apiClient';
import { hasPerm } from './utils/permissions';
import { asArray } from './utils/asArray';

const BUILD_STAMP = `v2025.12.16-${Date.now()}`;
console.log('SterlingOps', BUILD_STAMP);

function App() {
  const tokenAtStart = typeof window !== 'undefined' ? getToken() : null;
  const [currentStep, setCurrentStep] = useState<AppStep>(tokenAtStart ? AppStep.ADMIN_PORTAL : AppStep.LOGIN);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(!!tokenAtStart);
  
  // --- GLOBAL STATE (backed by API) ---
  const [siteConfig, setSiteConfig] = useState<SiteConfig>({
    appName: 'Sterling',
    appSubTitle: 'Discount Manager',
    logo: null,
    loginMessage: 'Authorized personnel only. Manage campaigns, submit discounts, and track approval workflows in real-time.',
    currencyCode: 'QAR'
  });

  const [campaigns, setCampaigns] = useState<Campaign[]>(INITIAL_CAMPAIGNS);
  const [brands, setBrands] = useState<Brand[]>(INITIAL_BRANDS);
  const [aggregators, setAggregators] = useState<Aggregator[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const campaignBrandCache = useRef<Record<string, string[]>>({});
  const savedProposalLogRef = useRef<boolean>(false);
  
  const [submissionHistory, setSubmissionHistory] = useState<SubmissionData[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const currencyCode = siteConfig.currencyCode || 'QAR';

  const normalizeSiteConfig = useCallback((config: SiteConfig): SiteConfig => ({
    ...config,
    currencyCode: config.currencyCode || 'QAR'
  }), []);

  const updateSiteConfig = useCallback((config: SiteConfig) => {
    setSiteConfig(normalizeSiteConfig(config));
  }, [normalizeSiteConfig]);

  // --- SESSION STATE ---
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Public Portal State
  const [selectedPublicCampaign, setSelectedPublicCampaign] = useState<Campaign | null>(null);

  // Editing State
  const [isEditingId, setIsEditingId] = useState<string | null>(null);

  // Form Data State
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [generalAggregators, setGeneralAggregators] = useState<string[]>([]);
  const [items, setItems] = useState<ItemDraft[]>([
    {
      id: Date.now().toString(),
      name: '',
      originalPrice: '',
      sellingPrice: '',
      description: '',
      image: null,
      imageName: undefined,
      selectedAggregators: [],
      managerNote: '',
      startDate: '',
      endDate: ''
    }
  ]);
  const [managerDetails, setManagerDetails] = useState<ManagerDetails>({
    name: '',
    fileNumber: '',
  });
  const [submittedData, setSubmittedData] = useState<SubmissionData | null>(null);
  const [editingProposalName, setEditingProposalName] = useState<string>('');
  const [, setIsHydrating] = useState(false);
  const [bundleEntries, setBundleEntries] = useState<BundleEntry[]>([]);
  const [activeBundleSubmissionId, setActiveBundleSubmissionId] = useState<string>('');
  const [bundleSubmitting, setBundleSubmitting] = useState(false);
  const [bundleSubmitResults, setBundleSubmitResults] = useState<Record<string, 'success' | 'error'>>({});

  const visibleCampaigns = useMemo(() => {
    if (currentUser?.role === UserRole.MANAGER && currentUser.brandIds && currentUser.brandIds.length > 0) {
      const allowed = new Set(currentUser.brandIds.filter(Boolean));
      return campaigns.filter((c) => {
        if (c.scope === 'ALL') return true;
        if (!c.brandIds || c.brandIds.length === 0) return true;
        return c.brandIds.some((id) => allowed.has(id));
      });
    }
    return campaigns;
  }, [campaigns, currentUser]);

  const visibleBrands = useMemo(() => {
    if (currentUser?.role === UserRole.MANAGER && currentUser.brandIds && currentUser.brandIds.length > 0) {
      const allowed = new Set(currentUser.brandIds);
      return brands.filter(b => allowed.has(b.id));
    }
    return brands;
  }, [brands, currentUser]);

  const brandMap = useMemo(() => {
    const map = new Map<string, Brand>();
    (brands || []).forEach((b) => {
      if (b.id) map.set(b.id, b);
    });
    return map;
  }, [brands]);

  const normalizeCampaign = (c: Campaign): Campaign => ({
    ...c,
    aggregators: Array.isArray(c.aggregators)
      ? c.aggregators
      : c.aggregators
      ? String(c.aggregators).split(',').map((a) => a.trim()).filter(Boolean)
      : [],
    brandIds: Array.isArray(c.brandIds)
      ? c.brandIds
      : c.brandIds
      ? [String(c.brandIds)]
      : c.brandIds,
    brands: Array.isArray((c as any).brands) ? (c as any).brands : [],
  });

  const normalizeCampaignList = (list: Campaign[] = []) => asArray(list).map((c) => normalizeCampaign(c));

  const normalizeItems = (raw: any): ItemDraft[] => {
    let val: any = raw;
    if (typeof val === 'string') {
      try {
        val = JSON.parse(val);
      } catch {
        val = [];
      }
    }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      val = Object.values(val);
    }
    if (!Array.isArray(val)) val = [];
    return val.map((item: any) => ({
      id: item?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: item?.name || '',
      originalPrice: item?.originalPrice ?? '',
      sellingPrice: item?.sellingPrice ?? '',
      description: item?.description,
      image: item?.image ?? item?.imageUrl ?? item?.image_url ?? item?.imagePreview ?? null,
      imageName: item?.imageName ?? item?.image_name ?? item?.imageFilename ?? '',
      selectedAggregators: Array.isArray(item?.selectedAggregators)
        ? item.selectedAggregators
        : item?.selectedAggregators
        ? Object.values(item.selectedAggregators)
        : [],
      managerNote: item?.managerNote || '',
      startDate:
        item?.startDate ??
        item?.start_date ??
        item?.start ??
        item?.timelineStart ??
        item?.proposalStartDate ??
        item?.proposal_start_date,
      endDate:
        item?.endDate ??
        item?.end_date ??
        item?.end ??
        item?.timelineEnd ??
        item?.proposalEndDate ??
        item?.proposal_end_date,
      choiceGroups: Array.isArray(item?.choiceGroups)
        ? item.choiceGroups
        : item?.choiceGroups
        ? Object.values(item.choiceGroups)
        : [],
    }));
  };

  const normalizeSubmission = (s: SubmissionData): SubmissionData => ({
    ...s,
    brands: Array.isArray(s.brands) ? s.brands : s.brands ? [String(s.brands)] : [],
    items: normalizeItems((s as any).items),
  });

  const normalizeSubmissionList = (list: SubmissionData[] = []) =>
    asArray(list).map((s) => normalizeSubmission(s));

  const getCampaignBrandIds = useCallback(
    (campaign: Campaign) => {
      const rawIds = Array.isArray(campaign.brandIds)
        ? campaign.brandIds
        : typeof (campaign as any).brandIds === 'string'
        ? [(campaign as any).brandIds]
        : [];
      const fallback = Array.isArray(campaign.brands)
        ? campaign.brands.map((b) => b.id).filter(Boolean)
        : [];
      const ids = rawIds.length ? rawIds : fallback;
      if (ids.length) return ids;
      const cached = campaignBrandCache.current[campaign.id];
      return cached || [];
    },
    []
  );

  const campaignBrandNames = useMemo(() => {
    if (!selectedPublicCampaign) return [];
    const ids = getCampaignBrandIds(selectedPublicCampaign);
    return asArray(ids)
      .map((id) => brandMap.get(id)?.name || id)
      .filter(Boolean);
  }, [brandMap, getCampaignBrandIds, selectedPublicCampaign]);

  const createEmptyItemDraft = (): ItemDraft => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    originalPrice: '',
    sellingPrice: '',
    description: '',
    image: null,
    imageName: undefined,
    selectedAggregators: [],
    managerNote: ''
  });

  const mapBundleItems = (items: any): ItemDraft[] => {
    let val = items;
    if (typeof val === 'string') {
      try {
        val = JSON.parse(val);
      } catch {
        val = [];
      }
    }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      val = Object.values(val);
    }
    if (!Array.isArray(val) || !val.length) return [createEmptyItemDraft()];
    return val.map((item: any) => ({
      id: item?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: item?.name || '',
      originalPrice: item?.originalPrice ?? '',
      sellingPrice: item?.sellingPrice ?? '',
      description: item?.description,
      image: item?.image || null,
      imageName: item?.imageName || undefined,
      selectedAggregators: Array.isArray(item?.selectedAggregators) ? item.selectedAggregators : [],
      managerNote: item?.managerNote || '',
      startDate: item?.startDate,
      endDate: item?.endDate,
    }));
  };

  const activeCampaigns = useMemo(
    () => asArray(visibleCampaigns).filter(c => c.status === CampaignStatus.ACTIVE),
    [visibleCampaigns]
  );
  const singleCampaignMode = activeCampaigns.length === 1;

  useEffect(() => {
    if (singleCampaignMode && !selectedPublicCampaign && currentStep === AppStep.WELCOME) {
      setSelectedPublicCampaign(activeCampaigns[0]);
    }
  }, [singleCampaignMode, selectedPublicCampaign, currentStep, activeCampaigns]);

  const hydrateAfterLogin = useCallback(async (user: User) => {
    setIsHydrating(true);
    try {
      const [subs, notifs, maybeUsers, aggs] = await Promise.all([
        apiClient.submissions.list().catch(() => ({ items: [] })),
        apiClient.notifications.list().catch(() => []),
        hasPerm(user, 'USERS') ? apiClient.users.list().catch(() => []) : Promise.resolve(null),
        apiClient.aggregators.list().catch(() => []),
      ]);
      const subsItems = Array.isArray((subs as any)?.items) ? (subs as any).items : Array.isArray(subs) ? subs : [];
      setSubmissionHistory(normalizeSubmissionList(subsItems));
      if (Array.isArray(notifs)) {
        const normalized = (notifs as any[]).map((n) => ({ ...n, read: Boolean((n as any).read) }));
        setNotifications(normalized as Notification[]);
      }
      if (Array.isArray(maybeUsers)) setUsers(maybeUsers);
      if (Array.isArray(aggs)) setAggregators(aggs as Aggregator[]);
    } finally {
      setIsHydrating(false);
    }
  }, []);

    // Hydrate user session from token
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsBootstrapping(false);
      return;
    }
    const hydrate = async () => {
      try {
        const response = await apiClient.auth.me();
        if (response?.user) {
          const user = response.user;
          console.log('Hydrated User:', user.name, 'Permissions:', user.permissions);
          setCurrentUser(user);
          await hydrateAfterLogin(user);
          // If user is already logged in, redirect to appropriate view
          if (user.role === UserRole.MANAGER || user.role === UserRole.DEPT_USER || user.role === UserRole.ADMIN || user.role === UserRole.SEO) {
             // UNIFIED: Everyone goes to the Portal structure now
             setCurrentStep(AppStep.ADMIN_PORTAL);
          }
        } else {
          removeToken();
        }
      } catch (error) {
        console.error('Session hydration failed', error);
        removeToken();
      } finally {
        setIsBootstrapping(false);
      }
    };
    hydrate();
  }, [hydrateAfterLogin]);

  // Sync campaigns from backend if available
  useEffect(() => {
    let cancelled = false;

      const syncCampaignsFromApi = async () => {
        try {
          const tokenPresent = !!getToken();
          if (!tokenPresent) return;
          const remoteCampaigns = await apiClient.campaigns.list();
          if (!cancelled && Array.isArray(remoteCampaigns)) {
            setCampaigns(normalizeCampaignList(remoteCampaigns));
          }
        } catch (error) {
          console.warn('Falling back to local campaigns. API unreachable.', error);
        }
      };

    syncCampaignsFromApi();
    return () => {
      cancelled = true;
    };
  }, [setCampaigns]);

  // Sync brands
  useEffect(() => {
    let cancelled = false;

    const syncBrands = async () => {
      try {
        const remoteBrands = await apiClient.brands.list();
        if (!cancelled && Array.isArray(remoteBrands) && remoteBrands.length > 0) {
          setBrands(remoteBrands);
        }
      } catch (error) {
        console.warn('Falling back to local brands. API unreachable.', error);
      }
    };

    syncBrands();
    return () => {
      cancelled = true;
    };
  }, [setBrands]);

  // Sync aggregators
  useEffect(() => {
    let cancelled = false;
    const syncAggregators = async () => {
      try {
        const remoteAggs = await apiClient.aggregators.list();
        if (!cancelled && Array.isArray(remoteAggs)) {
          setAggregators(remoteAggs as Aggregator[]);
        }
      } catch (error) {
        console.warn('Falling back to local aggregators. API unreachable.', error);
      }
    };
    syncAggregators();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync site config
  useEffect(() => {
    let cancelled = false;

    const syncSiteConfig = async () => {
      try {
        const remoteConfig = await apiClient.siteConfig.get();
        if (!cancelled && remoteConfig) {
          setSiteConfig(normalizeSiteConfig(remoteConfig));
        }
      } catch (error) {
        console.warn('Falling back to local site config. API unreachable.', error);
      }
    };

    syncSiteConfig();
    return () => {
      cancelled = true;
    };
  }, [setSiteConfig, normalizeSiteConfig]);

  // Sync submissions for dashboards
  useEffect(() => {
    let cancelled = false;
    if (!currentUser) return;

    const syncSubmissions = async () => {
      try {
        const remoteSubmissions = await apiClient.submissions.list();
        const items = Array.isArray((remoteSubmissions as any)?.items)
          ? (remoteSubmissions as any).items
          : Array.isArray(remoteSubmissions)
          ? remoteSubmissions
          : [];
        if (!cancelled) {
          setSubmissionHistory(normalizeSubmissionList(items));
        }
      } catch (error) {
        console.warn('Falling back to local submissions. API unreachable.', error);
      }
    };

    syncSubmissions();
    return () => {
      cancelled = true;
    };
  }, [setSubmissionHistory, currentUser]);

  // Poll for fresh notifications and submissions to mimic real-time
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const [latestNotifs, latestSubs] = await Promise.all([
          apiClient.notifications.list().catch(() => null),
          apiClient.submissions.list().catch(() => null),
        ]);
        if (!cancelled && Array.isArray(latestNotifs)) {
          const normalized = (latestNotifs as any[])
            .map((n) => ({ ...n, read: Boolean((n as any).read) }))
            .sort((a, b) => {
              const aTime = (a as any).time ? new Date((a as any).time).getTime() : 0;
              const bTime = (b as any).time ? new Date((b as any).time).getTime() : 0;
              return bTime - aTime;
            });
          setNotifications(normalized as Notification[]);
        }
        if (!cancelled) {
          const items = Array.isArray((latestSubs as any)?.items)
            ? (latestSubs as any).items
            : Array.isArray(latestSubs)
            ? latestSubs
            : [];
          setSubmissionHistory(normalizeSubmissionList(items));
        }
      } catch {
        // silent poll failure
      }
    };

    const interval = setInterval(poll, 10000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentUser]);

  // Sync users and notifications after login (admin-facing)
  useEffect(() => {
    let cancelled = false;
    if (!currentUser) return;

    const sync = async () => {
      if (hasPerm(currentUser, 'USERS')) {
        try {
          const remoteUsers = await apiClient.users.list();
          if (!cancelled && Array.isArray(remoteUsers)) {
            setUsers(remoteUsers);
          }
        } catch (error) {
          console.warn('Falling back to local users. API unreachable.', error);
        }
      }

      try {
        const remoteNotifications = await apiClient.notifications.list();
        if (!cancelled && Array.isArray(remoteNotifications)) {
          const normalized = (remoteNotifications as any[]).map((n) => ({ ...n, read: Boolean((n as any).read) }));
          setNotifications(normalized as Notification[]);
        }
      } catch (error) {
        console.warn('Falling back to local notifications. API unreachable.', error);
      }
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [currentUser, setUsers, setNotifications]);

  // Auto-select campaign if only one exists
  useEffect(() => {
    const scopedActive = asArray(visibleCampaigns).filter(c => c.status === CampaignStatus.ACTIVE);
    if (currentStep === AppStep.WELCOME && !selectedPublicCampaign && scopedActive.length === 1) {
        setSelectedPublicCampaign(scopedActive[0]);
    }
  }, [currentStep, visibleCampaigns, selectedPublicCampaign]);

  // Ensure manager selection stays within assigned brands
  useEffect(() => {
    if (!selectedPublicCampaign) return;
    if (currentUser?.role !== UserRole.MANAGER) return;
    const allowedIds = new Set(asArray(visibleCampaigns).map(c => c.id));
    if (!allowedIds.has(selectedPublicCampaign.id)) {
      setSelectedPublicCampaign(null);
    }
  }, [visibleCampaigns, selectedPublicCampaign, currentUser?.role]);

  // Helper: Toast Notification
  const showToast = useCallback((type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => {
    const id = Date.now().toString() + Math.random().toString();
    setToasts(prev => [...prev, { id, type, title, message }]);
  }, []);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const addNotification = async (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string, targetView?: AdminView, targetId?: string) => {
    const canPersist = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SEO;
    if (canPersist) {
      try {
        const updated = await apiClient.notifications.create({
          type,
          title,
          message,
          targetView,
          targetId,
        });
        if (Array.isArray(updated)) {
          const normalized = (updated as any[]).map((n) => ({ ...n, read: Boolean((n as any).read) }));
          setNotifications(normalized as Notification[]);
          return;
        }
      } catch (error) {
        console.warn('Failed to persist notification, using local fallback.', error);
      }
    }
    const newNotif: Notification = {
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        type,
        title,
        message,
        time: new Date().toISOString(),
        read: false,
        targetView,
        targetId
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  const markNotificationsRead = async () => {
      try {
        const updated = await apiClient.notifications.markRead();
        if (Array.isArray(updated)) {
          const normalized = (updated as any[]).map((n) => ({ ...n, read: Boolean((n as any).read) }));
          setNotifications(normalized as Notification[]);
          return;
        }
      } catch (error) {
        console.warn('Failed to mark notifications read on API.', error);
      }
      setNotifications(prev => prev.map(n => ({...n, read: true})));
  };

  const clearNotifications = () => {
    const clear = async () => {
      try {
        const updated = await apiClient.notifications.clear();
        if (Array.isArray(updated)) {
          const normalized = (updated as any[]).map((n) => ({ ...n, read: Boolean((n as any).read) }));
          setNotifications(normalized as Notification[]);
          return;
        }
      } catch (error) {
        console.warn('Failed to clear notifications on API.', error);
      }
      setNotifications([]);
    };
    clear();
  };

  const refreshSubmissions = useCallback(async () => {
    if (!currentUser) return;
    try {
      const remote = await apiClient.submissions.list();
      const items = Array.isArray((remote as any)?.items)
        ? (remote as any).items
        : Array.isArray(remote)
        ? remote
        : [];
      setSubmissionHistory(normalizeSubmissionList(items));
    } catch (error) {
      console.warn('Failed to refresh submissions', error);
    }
  }, [currentUser, normalizeSubmissionList]);

  const completeLogin = useCallback(
    async (user: User, options?: { silent?: boolean }) => {
      setCurrentUser(user);
      if (user.role === UserRole.MANAGER) {
        setManagerDetails({ name: user.name, fileNumber: user.id });
        if (!options?.silent) {
          showToast('success', 'Login Successful', `Welcome ${user.name}`);
        }
        setCurrentStep(AppStep.ADMIN_PORTAL);
      } else {
        if (!options?.silent) {
          showToast('success', 'Welcome Back', `Logged in as ${user.name} (${user.role})`);
        }
        setCurrentStep(AppStep.ADMIN_PORTAL);
      }
      await hydrateAfterLogin(user);
      window.scrollTo(0, 0);
    },
    [hydrateAfterLogin, showToast]
  );

  // Handlers
  const handleCampaignSelect = (campaign: Campaign) => {
    setSelectedPublicCampaign(campaign);
    window.scrollTo(0, 0);
  };

  const handleBackToCampaigns = () => {
    setSelectedPublicCampaign(null);
    setBundleEntries([]);
    setActiveBundleSubmissionId('');
    setBundleSubmitResults({});
    setCurrentStep(AppStep.WELCOME);
    window.scrollTo(0, 0);
  };

  const handleStart = async () => {
    if (!selectedPublicCampaign || !currentUser) return;
    setSelectedBrands([]);
    try {
      const res = await apiClient.submissions.startBundle(selectedPublicCampaign.id);
      const brands = asArray((res as any)?.brands);
      if (!brands.length) {
        showToast('error', 'No eligible brands', 'No eligible brands for this campaign.');
        return;
      }
      const entries: BundleEntry[] = brands.map((b: any) => ({
        brandId: b.brandId,
        brandName: b.brandName,
        submissionId: b.submissionId,
        status: b.status,
        items: mapBundleItems(b.items),
      }));
      if (!entries.length) {
        showToast('error', 'No eligible brands', 'No eligible brands for this campaign.');
        return;
      }
      setBundleEntries(entries);
      setActiveBundleSubmissionId(entries[0]?.submissionId);
      setBundleSubmitResults({});
      setCurrentStep(AppStep.BUNDLE);
      // Do NOT refresh submissions here, as we haven't saved anything yet.
      // await refreshSubmissions();
    } catch (e) {
      console.error('startBundle failed', e);
      showToast('error', 'Cannot start submissions', 'No eligible brands or server error.');
    }
    window.scrollTo(0, 0);
  };

  const handleBrandBack = () => {
    setCurrentStep(AppStep.WELCOME);
    window.scrollTo(0, 0);
  };

  const handleBrandSelect = (brandName: string) => {
    setSelectedBrands([brandName]);
  };

  const handleBrandNext = () => {
    if (selectedBrands.length > 0) {
      setCurrentStep(AppStep.ITEMS);
      window.scrollTo(0, 0);
    }
  };

  const handleItemsNext = () => {
    setCurrentStep(AppStep.CONFIRM);
    window.scrollTo(0, 0);
  };

  const handleItemsBack = () => {
    setCurrentStep(AppStep.BRAND);
    window.scrollTo(0, 0);
  };

  const handleConfirmBack = () => {
    setCurrentStep(AppStep.ITEMS);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      showToast('error', 'Login Required', 'Please sign in to submit a request.');
      setCurrentStep(AppStep.LOGIN);
      return;
    }

    // Determine which aggregators to apply to items
    // Priority: Campaign Aggregators > General Aggregators (Fallback/Edit)
    const campaignAggregators = asArray(selectedPublicCampaign?.aggregators || []);
    const aggregatorsToUse = campaignAggregators.length > 0 ? campaignAggregators : asArray(generalAggregators);

    const finalItems = asArray(items).map(item => ({
      ...item,
      selectedAggregators: aggregatorsToUse.length > 0 
        ? aggregatorsToUse 
        : item.selectedAggregators
    }));

    let finalData: SubmissionData;

    if (isEditingId) {
        // --- RESUBMISSION LOGIC ---
        const original = submissionHistory.find(s => s.submissionId === isEditingId);
        if (!original) return;

        const resubmitLog: WorkflowLog = {
            id: Date.now().toString(),
            stage: original.currentStage || WorkflowStage.RETURNED,
            action: 'Resubmit',
            comment: 'Edited and resubmitted by manager',
            user: currentUser?.name || 'Manager',
            timestamp: new Date().toLocaleString()
        };

        finalData = {
            ...original,
            brands: selectedBrands,
            items: finalItems,
            status: SubmissionStatus.PENDING,
            currentStage: WorkflowStage.F_AND_B,
            workflowLogs: [...(original.workflowLogs || []), resubmitLog],
            timestamp: new Date().toLocaleString()
        };

        let updatedSubmission = finalData;
        try {
          const response = await apiClient.submissions.update(isEditingId, finalData);
          updatedSubmission = response;
          await refreshSubmissions();
        } catch (error) {
          console.warn('API resubmit failed, keeping local changes.', error);
          setSubmissionHistory(prev => prev.map(s => s.submissionId === isEditingId ? updatedSubmission : s));
        }
        
        addNotification(
            'info', 
            'Submission Revised', 
            `Manager ${managerDetails.name} resubmitted #${isEditingId}.`,
            AdminView.SUBMISSIONS,
            isEditingId
        );

        showToast('success', 'Resubmitted!', 'Your revisions have been sent to the Admin team.');
        setCurrentStep(AppStep.ADMIN_PORTAL);

    } else {
        // --- NEW SUBMISSION LOGIC ---
        finalData = {
            brands: selectedBrands,
            items: finalItems,
            manager: managerDetails,
            managerId: currentUser?.id,
            submissionId: `SUB-${Math.floor(Math.random() * 10000)}`,
            timestamp: new Date().toLocaleString(),
            status: SubmissionStatus.PENDING,
            currentStage: WorkflowStage.F_AND_B,
            workflowLogs: [],
            campaignId: selectedPublicCampaign?.id,
            campaignName: selectedPublicCampaign?.name
        };
        
        try {
          const response = await apiClient.submissions.create(finalData);
          finalData = response;
          await refreshSubmissions();
        } catch (error) {
          console.warn('API submission failed, keeping local entry.', error);
          setSubmissionHistory(prev => [finalData, ...prev]);
        }
        
        addNotification(
            'info', 
            'New Campaign Submission', 
            `Manager ${managerDetails.name} submitted ${finalItems.length} items.`,
            AdminView.SUBMISSIONS,
            finalData.submissionId
        );

        setSubmittedData(finalData);
        showToast('success', 'Submission Sent!', 'Successfully forwarded to Admin.');
        setCurrentStep(AppStep.SUCCESS);
    }
    
    setIsEditingId(null);
    window.scrollTo(0, 0);
  };

  const handleReset = () => {
    setIsEditingId(null);
    setSelectedBrands([]);
    setGeneralAggregators([]);
    setItems([{
      id: Date.now().toString(),
      name: '',
      originalPrice: '',
      sellingPrice: '',
      description: '',
      image: null,
      imageName: undefined,
      selectedAggregators: [],
      managerNote: ''
    }]);
    if (currentUser && currentUser.role === UserRole.MANAGER) {
        setManagerDetails({ name: currentUser.name, fileNumber: currentUser.id });
    } else {
        setManagerDetails({ name: '', fileNumber: '' });
    }
    setSubmittedData(null);
    setSelectedPublicCampaign(null); 
    setEditingProposalName('');
    setBundleEntries([]);
    setActiveBundleSubmissionId('');
    setBundleSubmitResults({});
    setCurrentStep(AppStep.WELCOME); 
    window.scrollTo(0, 0);
  };

  const handleGoDashboard = () => {
    setCurrentStep(AppStep.ADMIN_PORTAL);
    window.scrollTo(0, 0);
  };

  const handleStartProposal = () => {
    setSelectedBrands([]); // Clear previous selection
    setEditingProposalName('');
    setCurrentStep(AppStep.PROPOSAL_BRAND);
    window.scrollTo(0, 0);
  };

  const handleBundleTabChange = (submissionId: string) => {
    setActiveBundleSubmissionId(submissionId);
  };

  const handleBundleItemsChange = (submissionId: string, items: ItemDraft[]) => {
    const safeItems = asArray(items);
    setBundleEntries((prev) =>
      prev.map((entry) => (entry.submissionId === submissionId ? { ...entry, items: safeItems } : entry))
    );
  };

  const buildBundlePayload = (entry: BundleEntry): SubmissionData => ({
    submissionId: entry.submissionId,
    managerId: currentUser?.id,
    manager: managerDetails,
    campaignId: selectedPublicCampaign?.id,
    campaignName: selectedPublicCampaign?.name,
    brands: [entry.brandName],
    items: entry.items,
    status: SubmissionStatus.PENDING,
    currentStage: WorkflowStage.F_AND_B,
    type: SubmissionType.CAMPAIGN,
  });

  const saveBundleDraft = async (submissionId: string) => {
    if (!currentUser || !selectedPublicCampaign) return;
    const entry = bundleEntries.find((e) => e.submissionId === submissionId);
    if (!entry) return;
    setBundleEntries((prev) =>
      prev.map((e) => (e.submissionId === submissionId ? { ...e, saving: true } : e))
    );
    try {
      // If it's a temporary DRAFT ID, we need to create it first
      if (submissionId.startsWith('DRAFT-')) {
         const payload = buildBundlePayload(entry);
         // Remove the temp ID so backend generates a real one, or handle it in create
         // Actually, create endpoint expects a payload. 
         // We should probably use create if it doesn't exist, or update if it does.
         // But since we don't know if it exists (it shouldn't if it's DRAFT-), we use create.
         // However, the backend create logic might need adjustment or we just send it.
         // Let's assume create handles it.
         const created = await apiClient.submissions.create({
             ...payload,
             submissionId: undefined // Let backend generate ID
         });
         
         // Update local state with real ID
         setBundleEntries(prev => prev.map(e => e.submissionId === submissionId ? { ...e, submissionId: created.submissionId } : e));
         // Also update active ID if needed
         if (activeBundleSubmissionId === submissionId) {
             setActiveBundleSubmissionId(created.submissionId);
         }
      } else {
         await apiClient.submissions.update(submissionId, buildBundlePayload(entry));
      }

      showToast('success', 'Draft saved', `Saved draft for ${entry.brandName}`);
      await refreshSubmissions();
    } catch (err) {
      console.error('saveBundleDraft failed', err);
      showToast('error', 'Save failed', `Could not save ${entry.brandName}`);
    } finally {
      setBundleEntries((prev) =>
        prev.map((e) => (e.submissionId === submissionId ? { ...e, saving: false } : e))
      );
    }
  };

  const validateBundleEntry = (entry: BundleEntry): string | null => {
    if (!entry.items || !entry.items.length) return 'Add at least one item';
    const hasMissing = entry.items.some(
      (it) => !String(it.name || '').trim() || !String(it.sellingPrice || '').trim()
    );
    if (hasMissing) return 'Each item needs a name and selling price';
    return null;
  };

  const submitAllBundles = async () => {
    if (!currentUser || !selectedPublicCampaign) return;
    for (const entry of bundleEntries) {
      const validation = validateBundleEntry(entry);
      if (validation) {
        showToast('error', `Fix ${entry.brandName}`, validation);
        setBundleSubmitResults((prev) => ({ ...prev, [entry.submissionId]: 'error' }));
        return;
      }
    }
    setBundleSubmitting(true);
    const results: Record<string, 'success' | 'error'> = {};
    for (const entry of bundleEntries) {
      try {
        if (entry.submissionId.startsWith('DRAFT-')) {
             const payload = buildBundlePayload(entry);
             await apiClient.submissions.create({ ...payload, submissionId: undefined });
        } else {
             await apiClient.submissions.update(entry.submissionId, buildBundlePayload(entry));
        }
        results[entry.submissionId] = 'success';
      } catch (err) {
        console.error('submitAllBundles entry failed', err);
        results[entry.submissionId] = 'error';
      }
    }
    setBundleSubmitResults(results);
    setBundleSubmitting(false);
    const allSuccess = Object.values(results).every((v) => v === 'success');
    if (allSuccess) {
      showToast('success', 'Submitted', 'All brand submissions sent.');
      await refreshSubmissions();
      setBundleEntries([]);
      setActiveBundleSubmissionId('');
      setBundleSubmitResults({});
      setCurrentStep(AppStep.ADMIN_PORTAL);
      setSelectedPublicCampaign(null);
    } else {
      showToast('error', 'Some submissions failed', 'Retry the tabs with errors.');
    }
  };

  const handleProposalBrandNext = () => {
    if (selectedBrands.length > 0) {
      setCurrentStep(AppStep.PROPOSAL);
      window.scrollTo(0, 0);
    }
  };

  const handleProposalSubmit = async (data: any) => {
    if (isEditingId) {
      const existing = submissionHistory.find(s => s.submissionId === isEditingId);
      if (!existing) return;

      const updatedProposal: SubmissionData = {
        ...existing,
        status: SubmissionStatus.PENDING,
        currentStage: WorkflowStage.F_AND_B,
        campaignName: data.proposalName || existing.campaignName,
        brands: data.brand ? [data.brand] : existing.brands,
        items: [{
          ...existing.items[0],
          name: data.itemName,
          originalPrice: data.currentPrice,
        sellingPrice: data.proposedPrice,
        description: data.description || '',
        image: data.image || existing.items[0].image,
        imageName: data.imageName || existing.items[0].imageName,
        managerNote: data.reason || '',
        startDate: data.startDate || existing.items[0].startDate,
        endDate: data.endDate || existing.items[0].endDate,
        selectedAggregators: Array.isArray(data.selectedAggregators) ? data.selectedAggregators : existing.items[0].selectedAggregators
      }],
      // Workflow logs are not updated via update endpoint, handled separately if needed
    };

    try {
      const saved = await apiClient.submissions.update(isEditingId, updatedProposal);
      if ((import.meta as any)?.env?.DEV && !savedProposalLogRef.current) {
        console.log('[ProposalSave] saved (update)', saved);
        savedProposalLogRef.current = true;
      }
      await refreshSubmissions();
      showToast('success', 'Proposal Updated', 'Your proposal has been resubmitted.');
    } catch (error) {
      console.error('Failed to update proposal', error);
      showToast('error', 'Update Failed', 'Could not save proposal.');
      }
    } else {
      const newSubmission: SubmissionData = {
        submissionId: `PROP-${Date.now().toString().slice(-6)}`,
        timestamp: new Date().toISOString(),
        status: SubmissionStatus.PENDING,
        currentStage: WorkflowStage.F_AND_B,
        manager: {
          name: currentUser?.name || 'Unknown',
          fileNumber: currentUser?.id || 'Unknown'
        },
        managerId: currentUser?.id,
        brands: data.brand ? [data.brand] : [],
        campaignName: data.proposalName || 'Unsolicited Proposal',
        type: SubmissionType.PROPOSAL,
        items: [{
          id: Date.now().toString(),
          name: data.itemName,
          originalPrice: data.currentPrice,
          sellingPrice: data.proposedPrice,
          description: data.description || '',
          image: data.image,
          imageName: data.imageName,
          selectedAggregators: Array.isArray(data.selectedAggregators) ? data.selectedAggregators : [],
          managerNote: data.reason || '',
          startDate: data.startDate,
          endDate: data.endDate
        }],
        workflowLogs: [] // Backend doesn't persist these on create
      };

      try {
        const saved = await apiClient.submissions.create(newSubmission);
        if ((import.meta as any)?.env?.DEV && !savedProposalLogRef.current) {
          console.log('[ProposalSave] saved (create)', saved);
          savedProposalLogRef.current = true;
        }
        await refreshSubmissions();
        showToast('success', 'Proposal Sent', 'Your proposal has been submitted for review.');
        addNotification(
            'info', 
            'New Proposal Submitted', 
            `Proposal ${newSubmission.submissionId} sent for review.`,
            AdminView.PROPOSALS,
            newSubmission.submissionId
        );
      } catch (error) {
        console.error('Failed to create proposal', error);
        showToast('error', 'Submission Failed', 'Could not save proposal.');
      }
    }
    setCurrentStep(AppStep.ADMIN_PORTAL);
    setIsEditingId(null);
    setEditingProposalName('');
  };

  // --- UNIFIED LOGIN HANDLER ---
  const handleLogin = async (username: string, password: string) => {
    const auth = await apiClient.auth.login(username, password);
    await completeLogin(auth.user);
  };

  const handleRegister = async (userData: Partial<User> & { password: string }) => {
    await apiClient.auth.register({
      name: userData.name!,
      email: userData.email!,
      username: userData.username!,
      password: userData.password!,
      role: userData.role ?? UserRole.DEPT_USER,
      department: userData.department ?? Department.ALL,
    });
    showToast('info', 'Registration Submitted', 'Your account is pending approval by an administrator.');
  };

  const handleLogout = () => {
    apiClient.auth.logout().catch(() => null);
    removeToken();
    setCurrentUser(null);
    setSubmissionHistory([]);
    setNotifications([]);
    setUsers([]);
    setBundleEntries([]);
    setActiveBundleSubmissionId('');
    setBundleSubmitResults({});
    showToast('info', 'Logged Out', 'See you next time.');
    setCurrentStep(AppStep.LOGIN); // Go to Unified Login
    setIsEditingId(null);
    setSelectedPublicCampaign(null);
    window.scrollTo(0, 0);
  };

  const handleEditSubmission = (submission: SubmissionData) => {
      // STRICT EDIT LOGIC:
      // Managers can only edit if:
      // 1. Returned (Needs Revision)
      // 2. Pending AND still in F&B stage (hasn't moved to Finance/Digital)
      
      const isNeedsRevision = submission.status === SubmissionStatus.NEEDS_REVISION;
      const isPending = submission.status === SubmissionStatus.PENDING;
      const currentStage = submission.currentStage || WorkflowStage.F_AND_B;
      
      const isLocked = isPending && currentStage !== WorkflowStage.F_AND_B;
      const isFinal = submission.status === SubmissionStatus.APPROVED || submission.status === SubmissionStatus.EXPORTED || submission.status === SubmissionStatus.REJECTED;

      if (isLocked || isFinal) {
          showToast('error', 'Action Denied', 'This submission is locked by approval or finalized.');
          return;
      }

      setIsEditingId(submission.submissionId || null);
      setSelectedBrands(submission.brands);
      setItems(submission.items);
      setManagerDetails(submission.manager);
      setEditingProposalName(submission.type === SubmissionType.PROPOSAL ? (submission.campaignName || '') : '');
      
      // Populate generalAggregators from the items so the Confirm screen checkboxes are checked (if falling back)
      if (submission.items.length > 0 && submission.items[0].selectedAggregators) {
          setGeneralAggregators(submission.items[0].selectedAggregators);
      } else {
          setGeneralAggregators([]);
      }

      if (submission.type === SubmissionType.PROPOSAL) {
          setCurrentStep(AppStep.PROPOSAL);
          showToast('info', 'Editing Proposal', 'You can now modify your proposal.');
          window.scrollTo(0,0);
          return;
      }

        setEditingProposalName('');

      const campaign = campaigns.find(c => c.id === submission.campaignId) || campaigns[0];
      setSelectedPublicCampaign(campaign);
      setCurrentStep(AppStep.ITEMS); 
      showToast('info', 'Editing Mode', 'You can now modify and resubmit this request.');
      window.scrollTo(0,0);
  };

  const handleSwitchToPublic = () => {
    showToast('info', 'Public View', 'You are now viewing the public site as an admin.');
    setCurrentStep(AppStep.WELCOME);
    setSelectedPublicCampaign(null);
    window.scrollTo(0, 0);
  }

  const handleUpdateCurrentUser = (updatedUser: User) => {
      setCurrentUser(updatedUser);
      setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
  };

  // --- BOOTSTRAP LOADER (prevents login flash on refresh) ---
  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
        <div className="w-14 h-14 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4" />
        <p className="text-sm font-semibold text-zinc-500">Restoring your session...</p>
      </div>
    );
  }

  // --- RENDER LOGIN (Unified) ---
  if (currentStep === AppStep.LOGIN || currentStep === AppStep.ADMIN_LOGIN || currentStep === AppStep.MANAGER_LOGIN) {
      return (
        <>
          <UnifiedLogin 
            onLogin={handleLogin}
            onRegister={handleRegister}
            showToast={showToast}
            siteConfig={siteConfig}
          />
          <ToastContainer toasts={toasts} removeToast={removeToast} />
        </>
      );
  }

  // --- RENDER ADMIN PORTAL (Unified for all authenticated users) ---
  if (currentStep === AppStep.ADMIN_PORTAL && currentUser) {
      return (
        <>
          <AdminPortal 
              onLogout={handleLogout} 
              onViewPublicSite={handleSwitchToPublic}
              campaigns={visibleCampaigns}
              onUpdateCampaigns={setCampaigns}
              submissions={submissionHistory}
              onUpdateSubmissions={setSubmissionHistory}
              brands={brands} 
              onUpdateBrands={setBrands}
              aggregators={aggregators}
              onUpdateAggregators={setAggregators}
              siteConfig={siteConfig} 
              onUpdateSiteConfig={updateSiteConfig} 
              notifications={notifications}
              onMarkAllRead={markNotificationsRead}
              onClearAll={clearNotifications}
              onAddNotification={addNotification}
              showToast={showToast}
              users={users}
              onUpdateUsers={setUsers}
              currentUser={currentUser}
              onUpdateUser={handleUpdateCurrentUser}
              // Pass Manager Handlers
              onNewSubmission={handleReset}
              onEditSubmission={handleEditSubmission}
              onNewProposal={handleStartProposal}
          />
          <ToastContainer toasts={toasts} removeToast={removeToast} />
        </>
      );
  }

  // --- RENDER MANAGER / PUBLIC FLOW ---
  return (
    <div className="min-h-screen relative overflow-hidden font-sans text-zinc-900 selection:bg-teal-100 selection:text-teal-900">
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Abstract Background Shapes */}
      <div className="fixed top-[-10%] right-[-10%] w-[500px] h-[500px] bg-teal-400/20 rounded-full blur-3xl mix-blend-multiply filter animate-blob" />
      <div className="fixed top-[-10%] left-[-10%] w-[500px] h-[500px] bg-cyan-400/20 rounded-full blur-3xl mix-blend-multiply filter animate-blob animation-delay-2000" />
      <div className="fixed bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-emerald-400/20 rounded-full blur-3xl mix-blend-multiply filter animate-blob animation-delay-4000" />

      {/* Header */}
      <header className="fixed top-0 z-50 w-full transition-all duration-300 pointer-events-none">
        <div className="w-full px-6 md:px-10 h-28 flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-4 bg-white/80 backdrop-blur-md px-5 py-3 rounded-2xl shadow-lg shadow-zinc-200/20 border border-white/50">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-teal-200 overflow-hidden">
                {siteConfig.logo ? <img src={siteConfig.logo} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center">{siteConfig.appName.charAt(0)}</div>}
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-extrabold tracking-tight text-zinc-800 leading-none mb-0.5">{siteConfig.appName}</h1>
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest leading-none">{siteConfig.appSubTitle}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Step Indicator only visible during submission flow */}
            {(currentStep === AppStep.BRAND || currentStep === AppStep.ITEMS || currentStep === AppStep.CONFIRM) && (
               <StepIndicator currentStep={currentStep} />
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-32 pb-20 px-4 sm:px-6">
        <div className="mx-auto transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] max-w-6xl">
          
          {currentStep === AppStep.PROPOSAL && (
            <div className="animate-fade-in-up">
               <ProposalForm 
                onBack={() => setCurrentStep(AppStep.PROPOSAL_BRAND)}
                onSubmit={handleProposalSubmit}
                selectedBrand={selectedBrands[0]}
                initialData={isEditingId ? items[0] : undefined}
                initialProposalName={editingProposalName}
                aggregatorOptions={asArray(aggregators).map(a => ({ id: a.id, name: a.name, active: a.active }))}
                  currencyCode={currencyCode}
              />
            </div>
          )}

          {currentStep === AppStep.BUNDLE && selectedPublicCampaign && bundleEntries.length > 0 && (
            <div className="animate-fade-in-up max-w-4xl mx-auto px-3">
              <ErrorBoundary fallback={<div className="text-sm text-red-600">Something went wrong. Please reload.</div>}>
                <ManagerSubmissionBundleView
                  campaign={selectedPublicCampaign}
                  entries={bundleEntries}
                  activeSubmissionId={activeBundleSubmissionId || bundleEntries[0].submissionId}
                  onSelectTab={handleBundleTabChange}
                  onItemsChange={handleBundleItemsChange}
                  onSaveDraft={saveBundleDraft}
                  onSubmitAll={submitAllBundles}
                  submittingAll={bundleSubmitting}
                  managerDetails={managerDetails}
                  setManagerDetails={setManagerDetails}
                  currencyCode={currencyCode}
                  submitResults={bundleSubmitResults}
                  onBack={handleBackToCampaigns}
                />
              </ErrorBoundary>
            </div>
          )}

          {currentStep === AppStep.PROPOSAL_BRAND && (
            <div className="animate-fade-in-up max-w-4xl mx-auto">
              <BrandSelection 
                brands={visibleBrands} 
                onSelect={handleBrandSelect} 
                selectedBrands={selectedBrands} 
                onNext={handleProposalBrandNext}
                onBack={handleGoDashboard}
              />
            </div>
          )}

          {/* View 2: Active Campaign Grid */}
          {currentStep === AppStep.WELCOME && (
            !selectedPublicCampaign ? (
                <ActiveCampaignGrid 
                  campaigns={visibleCampaigns} 
                  brands={brands}
                  onSelect={handleCampaignSelect} 
                  onDashboard={currentUser ? handleGoDashboard : undefined}
                  currencyCode={currencyCode}
                />
            ) : (
                // View 3: Campaign Detail
                <div className="flex flex-col items-center animate-fade-in-up space-y-10 max-w-4xl mx-auto">
                    <div className="w-full flex justify-between">
                        {!singleCampaignMode ? (
                            <button onClick={handleBackToCampaigns} className="flex items-center gap-2 text-zinc-500 hover:text-teal-600 font-bold transition-all hover:-translate-x-1">
                                <ArrowLeft className="w-5 h-5" /> Back to Campaigns
                            </button>
                        ) : <div />}
                        <button onClick={handleGoDashboard} className="flex items-center gap-2 text-zinc-500 hover:text-teal-600 font-bold transition-all">
                            Dashboard <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                    <CampaignNote campaign={selectedPublicCampaign} currencyCode={currencyCode} brandNames={campaignBrandNames} />
                    
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full blur opacity-40 group-hover:opacity-75 transition duration-200" />
                        <button
                            onClick={handleStart}
                            className="relative flex items-center gap-3 px-10 py-5 bg-white text-zinc-900 rounded-full font-bold text-lg shadow-xl hover:-translate-y-1 transition-all duration-200 active:scale-95"
                        >
                            Start Submission
                            <div className="bg-zinc-100 rounded-full p-2 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                            <ArrowRight className="w-5 h-5" />
                            </div>
                        </button>
                    </div>
                </div>
            )
          )}

          {/* Submission Flow Steps */}
          {currentStep === AppStep.BRAND && (
            <div className="animate-fade-in-up max-w-4xl mx-auto">
              <BrandSelection 
                brands={visibleBrands} 
                onSelect={handleBrandSelect} 
                selectedBrands={selectedBrands} 
                onNext={handleBrandNext}
                onBack={handleBrandBack}
              />
            </div>
          )}

          {currentStep === AppStep.ITEMS && (
            <div className="animate-fade-in-up max-w-4xl mx-auto">
               <ItemForm 
                brandNames={selectedBrands}
                items={items}
                setItems={setItems}
                onNext={handleItemsNext}
                  onBack={handleItemsBack}
                  currencyCode={currencyCode}
               />
            </div>
          )}

          {currentStep === AppStep.CONFIRM && (
             <div className="animate-fade-in-up max-w-4xl mx-auto">
                <IdentityConfirm 
                  brandNames={selectedBrands}
                  items={items}
                  managerDetails={managerDetails}
                  setManagerDetails={setManagerDetails}
                  generalAggregators={generalAggregators}
                  setGeneralAggregators={setGeneralAggregators}
                  aggregators={aggregators}
                  onBack={handleConfirmBack}
                  onSubmit={handleSubmit}
                  currencyCode={currencyCode}
                  campaign={selectedPublicCampaign} // Pass campaign details
                />
             </div>
          )}

          {currentStep === AppStep.SUCCESS && submittedData && (
             <div className="animate-fade-in-up max-w-4xl mx-auto">
                <SuccessScreen 
                  data={submittedData} 
                  onReset={handleReset} 
                  onHome={handleGoDashboard} 
                  showToast={showToast}
                  currencyCode={currencyCode}
                />
             </div>
          )}

        </div>
      </main>
      {/* Build stamp footer for cache verification */}
      <footer className="fixed bottom-0 right-0 p-1 text-xs text-gray-400 opacity-50 z-50">
        {BUILD_STAMP}
      </footer>
    </div>
  );
}

export default App;
